import React, { useMemo, useCallback } from 'react';
import CodeTag from './CodeTag';

/**
 * Component to render a list of code tags for an excerpt
 * 
 * @param {Object} props - Component props
 * @param {Array} props.codings - Array of coding objects
 * @param {string|number} props.excerptId - ID of the excerpt
 * @param {boolean} props.codePage - Whether this is on a code page
 * @param {boolean} props.selected - Whether the excerpt is selected
 * @param {boolean} props.hasEditPermission - Whether the user has edit permission
 * @param {Function} props.onCodeRemove - Function to call when a code is removed
 * @param {Function} props.onCodeClick - Function to call when a code is clicked
 * @param {string|number} props.projectID - ID of the project
 * @returns {JSX.Element} The rendered component
 */
const ExcerptCodesList = ({
  codings = [],
  excerptId,
  codePage,
  selected,
  hasEditPermission,
  onCodeRemove,
  onCodeClick,
  projectID
}) => {
  // Group codings by code_id and create coders array
  const groupedCodings = useMemo(() => {
    const grouped = {};
    
    codings.forEach(coding => {
      const { code_id, user_id, created_at } = coding;
      
      if (!grouped[code_id]) {
        grouped[code_id] = {
          code_id,
          coders: []
        };
      }
      
      grouped[code_id].coders.push({
        user_id,
        time: created_at
      });
    });
    
    return Object.values(grouped);
  }, [codings]);

  // Handle code removal
  const handleCodeRemove = useCallback((codeId) => {
    onCodeRemove && onCodeRemove(codeId, excerptId);
  }, [onCodeRemove, excerptId]);

  return (
    <>
      {groupedCodings.map((coding, index) => (
        <CodeTag
          id={coding.code_id}
          codePage={codePage}
          key={coding.code_id} // Use code_id as key for better performance
          projectID={projectID}
          onCodeRemove={handleCodeRemove}
          selected={selected}
          hasEditPermission={hasEditPermission}
          onClick={onCodeClick}
          coders={coding.coders}
          inExcerpt={true}
        />
      ))}
    </>
  );
};

export default ExcerptCodesList;